import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import {
    AddressParams,
    createAccountAddress,
    deleteAccountAddress,
    fetchAccountAddress,
    updateAccountAddress,
} from 'api/account';
import { AddressFormValues } from 'components/address-form/types';
import { useGivebutterForm } from 'components/form/useGivebutterForm';
import { AddressType } from 'constants/addresses';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useFormServerErrors } from 'customHooks/useFormServerErrors';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Address } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface UseAccountAddressFormProps {
    addressId?: number;
    isMailingAddress: boolean;
    onSuccess?: (newAddress: Address) => void;
}

const useAccountAddressForm = (props: UseAccountAddressFormProps = { isMailingAddress: false }) => {
    const { addressId, onSuccess, isMailingAddress } = props;
    const toast = useToast();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const isEditing = !!addressId;
    const { data: address } = useSuspenseQuery({
        queryKey: accountQueryKeys.addressById(accountId, Number(addressId)),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchAccountAddress(accountId, addressId ?? 0),
                isEditing,
            ),
    });

    const methods = useGivebutterForm<AddressFormValues>({
        defaultValues: {
            line1: address?.address_1 ?? '',
            line2: address?.address_2 ?? '',
            city: address?.city ?? '',
            zip: address?.zipcode ?? '',
            stateUs: address?.country === 'USA' ? (address?.state ?? '') : '',
            stateIntl: address?.country !== 'USA' ? (address?.state ?? '') : '',
            country: address?.country ?? 'USA',
            isPrimary: address?.is_primary ?? !isMailingAddress,
            type: isMailingAddress ? AddressType['Mailing'] : AddressType['Work'],
            name: address?.name ?? '',
        },
    });
    const { setAddressErrors } = useFormServerErrors(methods);

    const createAddressMutation = useMutation({
        mutationFn: (values: AddressParams) => createAccountAddress(accountId, values),
        onSuccess: newAddress => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
                type: 'all',
            });
            onSuccess?.(newAddress);
            toast.success('Address added successfully!');
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;
            if (errors) {
                setAddressErrors(errors);
            }
            toast.error(<>{error.response?.data.message ?? 'Unknown error occurred'}</>);
        },
    });

    const updateAddressMutation = useMutation({
        mutationFn: (values: AddressParams) =>
            updateAccountAddress(accountId, address?.id ?? 0, values),
        onSuccess: newAddress => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
                type: 'all',
            });
            onSuccess?.(newAddress);
            toast.success('Address updated successfully!');
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;
            if (errors) {
                setAddressErrors(errors);
            }
            toast.error(<>{error.response?.data.message ?? 'Unknown error occurred'}</>);
        },
    });

    const createOrUpdateMutation = isEditing ? updateAddressMutation : createAddressMutation;

    const deleteAccountAddressMutation = useMutation({
        mutationFn: (addressId: number) => deleteAccountAddress(accountId, addressId),
        onSuccess: (_, addressId) => {
            queryClient.removeQueries({
                queryKey: accountQueryKeys.addressById(accountId, addressId),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
                type: 'all',
            });
            toast.success('Address deleted successfully!');
        },
        onError: (error: GivebutterAxiosError) =>
            toast.error(<>{error.response?.data.message ?? 'Unknown error occurred'}</>),
    });

    const setPrimaryAddressMutation = useMutation({
        mutationFn: (params: { addressId: number; isPrimary: boolean }) =>
            updateAccountAddress(accountId, params.addressId, { isPrimary: params.isPrimary }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
                type: 'all',
            });
            toast.success('Updated primary address successfully!');
        },
        onError: (error: GivebutterAxiosError) =>
            toast.error(<>{error.response?.data.message ?? 'Unknown error occurred'}</>),
    });

    return {
        methods,
        createOrUpdateMutation,
        deleteAccountAddressMutation,
        setPrimaryAddressMutation,
    };
};

const transformAddressFormToRequest = <
    T extends AddressFormValues,
    K extends Omit<T, 'stateUs' | 'stateIntl'> & AddressParams,
>(
    formValues: T,
): K => {
    const { stateUs, stateIntl, ...values } = formValues;
    const state = values.country === 'USA' ? stateUs : stateIntl;

    return {
        ...values,
        state,
    } as K;
};

const transformServerAddressIntoFormAddress = (address: Address) => {
    return {
        line1: address.address_1 ?? '',
        line2: address.address_2 ?? '',
        city: address.city ?? '',
        zip: address.zipcode ?? '',
        stateUs: address.country === 'USA' ? address.state : '',
        stateIntl: address.country !== 'USA' ? address.state : '',
        country: address.country ?? 'USA',
        isPrimary: address.is_primary ?? true,
        type: address.type ?? AddressType['Work'],
        name: address.name ?? '',
    };
};

export {
    useAccountAddressForm,
    transformAddressFormToRequest,
    transformServerAddressIntoFormAddress,
};
